import React from 'react';
import { clsx } from 'clsx';
import styles from './InputRadio.module.css';
import { FormErrorMessage } from '../form-error-message';
import { useRadioError } from './hooks/InputRadioHook';
import { InputRadioErrorContextProvider } from './hooks/InputRadioContext';

export type InputRadioProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
	/**
	 * Identifies if the radio button is default checked
	 */
	defaultChecked?: boolean;
	/**
	 * Indicates the text or node to be used in the input label
	 */
	label: React.ReactNode;

	/**
	 * Description of the property
	 * @type string
	 */
	name: string;

	/**
	 * Value of the input
	 * @type string
	 */
	value?: string;

	/**
	 * Identifier of the component
	 */
	id?: string;

	/**
	 * Styles of the container of the input
	 */
	className?: string;

	/**
	 * Styles custom of the  radioButton
	 */
	stylesRadio?: string;
};

/**
 * Standalone input Radio, it should be used inside an InputRadioGroup, the errorMessage should be communicated only to InputRadioGroup
 */
export const InputRadio = React.forwardRef<HTMLInputElement, InputRadioProps>(
	({ label, name, id, className, stylesRadio, value, ...props }, ref) => {
		const generateIdentifier = React.useId();
		const identifier = id ?? generateIdentifier;
		const errorMessage: string | undefined = useRadioError();

		return (
			<div className={clsx(styles.inputContainer, className)}>
				<input
					className={clsx(styles.input, stylesRadio)}
					ref={ref}
					type="radio"
					name={name}
					value={value}
					id={identifier + '-input'}
					data-state-invalid={Boolean(errorMessage)}
					aria-describedby={errorMessage}
					{...props}
				/>
				<label className={styles.label} htmlFor={identifier + '-input'}>
					{label}
				</label>
			</div>
		);
	}
);
InputRadio.displayName = 'InputRadio';

export type InputRadioGroupProps = React.DetailedHTMLProps<
	React.InputHTMLAttributes<HTMLDivElement>,
	HTMLDivElement
> & {
	/**
	 * Indicates the direction that the radio buttons inside will have
	 * @default horizontal
	 */
	direction?: 'horizontal' | 'vertical';

	/**
	 * Pass the error message to indicate there is an error and show it. It will be automatically communicate for all radio input children
	 * @default horizontal
	 */
	errorMessage?: React.ReactNode;

	/**
	 * Pass some children to this container. Normally you wan them to be radio inputs.
	 */
	children: React.ReactNode;

	/**
	 * Identifier of the component
	 */
	id?: string;

	/**
	 * Required label for accessibility, following the aria pattern.
	 * @see https://www.w3.org/WAI/ARIA/apg/patterns/radio/
	 */
	ariaLabel: string;

	/**
	 * Personalize styles of container, like gap.
	 */
	className?: string;
};

export const InputRadioGroup = React.forwardRef<HTMLDivElement, InputRadioGroupProps>(
	({ direction = 'horizontal', children, id, className, errorMessage, ariaLabel, ...props }, ref) => {
		const generateIdentifier = React.useId();
		const identifier = id ?? generateIdentifier;
		const errorMessageIdentifier = errorMessage ? identifier + '-error' : undefined;

		return (
			<InputRadioErrorContextProvider errorMessage={errorMessageIdentifier}>
				<div
					className={clsx(styles.radioGroupContainer, className)}
					{...props}
					ref={ref}
					role="radiogroup"
					aria-errormessage={errorMessage ? identifier + '-error' : undefined}
					aria-invalid={Boolean(errorMessage)}
					aria-label={ariaLabel}
				>
					<div
						className={clsx(styles.radioGroupChildren, direction === 'vertical' && styles.radioGroupChildrenVertical)}
					>
						{children}
					</div>
					{errorMessage && <FormErrorMessage id={errorMessageIdentifier}>{errorMessage}</FormErrorMessage>}
				</div>
			</InputRadioErrorContextProvider>
		);
	}
);
InputRadioGroup.displayName = 'InputRadioGroup';
